import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));

    // Listen for login/logout to refresh token state
    useEffect(() => {
        const checkToken = () => {
            const currentToken = localStorage.getItem('token');
            if (currentToken !== token) {
                setToken(currentToken);
            }
        };

        // Check frequently and on storage events
        const interval = setInterval(checkToken, 2000);
        window.addEventListener('storage', checkToken);

        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', checkToken);
        };
    }, [token]);

    useEffect(() => {
        if (!token) {
            if (socket) {
                socket.disconnect();
                setSocket(null);
            }
            return;
        }

        const getSocketConfig = () => {
            const hostname = window.location.hostname || "localhost";
            const isLocal =
                hostname === "localhost" ||
                hostname === "127.0.0.1" ||
                hostname.startsWith("192.168.") ||
                hostname.startsWith("10.") ||
                hostname.startsWith("172.");

            if (process.env.REACT_APP_SOCKET_URL) {
                return {
                    url: process.env.REACT_APP_SOCKET_URL,
                    path: process.env.REACT_APP_SOCKET_PATH || (isLocal ? '/socket.io' : '/api/socket.io'),
                };
            }

            return {
                url: isLocal ? `http://${hostname}:3001` : 'https://app.areatrek.com',
                path: isLocal ? '/socket.io' : '/api/socket.io',
            };
        };

        const { url: socketUrl, path: socketPath } = getSocketConfig();
        console.log(`🔌 Initializing Admin Socket connection to: ${socketUrl} (path: ${socketPath})`);

        const newSocket = io(socketUrl, {
            path: socketPath,
            auth: { token },
            query: { token },
            extraHeaders: {
                Authorization: `Bearer ${token}`,
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 20,
            reconnectionDelay: 1000,
        });

        newSocket.on('connect', () => {
            console.log('✅ Connected to Admin Socket (id:', newSocket.id, ')');
        });

        newSocket.on('disconnect', (reason) => {
            console.warn('⚠️ Admin Socket disconnected:', reason);
        });

        // Listen for real-time session termination
        newSocket.on('session_terminated', (data) => {
            Swal.fire({
                title: 'Session Terminated',
                text: data.message || 'Your session has been terminated by an administrator.',
                icon: 'warning',
                confirmButtonColor: '#d33',
                confirmButtonText: 'Login Again',
                allowOutsideClick: false,
            }).then(() => {
                localStorage.removeItem('token');
                window.location.href = '/auth/login';
            });
        });

        newSocket.on('connect_error', (err) => {
            console.error('❌ Socket Connection Error:', err.message);
            if (err.message === "Authentication error: Invalid or terminated session") {
                // Fallback catch for invalid tokens on connection
                localStorage.removeItem('token');
                window.location.href = '/auth/login';
            }
        });

        setSocket(newSocket);

        return () => {
            if (newSocket) newSocket.disconnect();
        };
    }, [token]);

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};
